/**
 * Search utility functions for improved relevance-based searching
 */

/**
 * Calculates relevance score for a search term against a text
 * Higher scores indicate better matches
 * @param {string} searchTerm - The term being searched for
 * @param {string} text - The text being searched in
 * @returns {number} - Relevance score (0 = no match, higher = better match)
 */
export const calculateSearchRelevance = (searchTerm, text) => {
  if (!searchTerm || !text) return 0;
  
  const searchLower = searchTerm.toLowerCase().trim();
  const textLower = text.toLowerCase().trim();
  
  // No match
  if (!textLower.includes(searchLower)) return 0;
  
  // Exact match (highest priority)
  if (textLower === searchLower) return 100;
  
  // Starts with search term (very high priority)
  if (textLower.startsWith(searchLower)) return 90;
  
  // Word boundary match (high priority)
  const wordBoundaryRegex = new RegExp(`\\b${searchLower}\\b`, 'i');
  if (wordBoundaryRegex.test(textLower)) return 80;
  
  // Starts with word that contains search term (medium-high priority)
  const words = textLower.split(/\s+/);
  for (const word of words) {
    if (word.startsWith(searchLower)) return 70;
  }
  
  // Contains search term (lowest priority, but still a match)
  return 10;
};

/**
 * Filters and sorts items based on search relevance
 * @param {Array} items - Array of items to search through
 * @param {string} searchTerm - Search term
 * @param {Array} searchFields - Array of field names to search in
 * @returns {Array} - Filtered and sorted items by relevance
 */
export const searchWithRelevance = (items, searchTerm, searchFields) => {
  if (!searchTerm || !searchTerm.trim()) return items;
  
  const searchLower = searchTerm.toLowerCase().trim();
  
  // Calculate relevance scores for each item
  const itemsWithScores = items.map(item => {
    let maxScore = 0;
    
    // Check each search field and take the highest score
    for (const field of searchFields) {
      const fieldValue = item[field];
      if (fieldValue) {
        const score = calculateSearchRelevance(searchLower, fieldValue);
        maxScore = Math.max(maxScore, score);
      }
    }
    
    return { ...item, _relevanceScore: maxScore };
  });
  
  // Filter out items with no matches and sort by relevance (highest first)
  return itemsWithScores
    .filter(item => item._relevanceScore > 0)
    .sort((a, b) => b._relevanceScore - a._relevanceScore)
    .map(({ _relevanceScore, ...item }) => item); // Remove the score from final result
};
